import useTranslation from '../useTranslation';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { DEFAULT_CENTER, DEFAULT_ZOOM, MAP_STYLE } from 'components/Map/constants';

import useApplications from 'hooks/useApplications';
import type { ApplicationResponse } from 'hooks/useApplications/types';
import useBoundary from 'hooks/useBoundary';
import useField from 'hooks/useField';
import { useFloatingResourceAddContext } from 'hooks/useFloatingResourceAddContext';
import { useNavigationCollapse } from 'hooks/useNavigationCollapse';
import usePageRoutes from 'hooks/usePageRoutes';

import {
    DETAIL_TYPE_CITY,
    DETAIL_TYPE_CLIENT,
    DETAIL_TYPE_FARM,
    DETAIL_TYPE_FIELD,
    DETAIL_TYPE_STATE,
    DETAIL_TYPE_ZIP
} from 'pages/BoundaryDetailPage/constants';

import type {
    BoundaryResponse,
    CreateBoundaryRequest,
    DetailsViewEntityLinkProps,
    MapInteractionOptions,
    MapOptions,
    SiteWithParcelAndClientResponse
} from './types';

const useData = () => {
    const { boundaryId } = useParams();
    const { state } = useLocation();
    const { EDIT_BOUNDARY } = useTranslation();
    const { fetchBoundary, isLoading, updateBoundary } = useBoundary();
    const { fetchField, isLoading: isFieldLoading } = useField();
    const {
        applications,
        fetch: fetchApplications,
        isLoading: isApplicationsLoading
    } = useApplications();
    const { boundariesPage, clientDetailsPage, farmDetailsPage, fieldDetailsPage } =
        usePageRoutes();
    const navigate = useNavigate();
    const [currentBoundary, setCurrentBoundary] = useState<BoundaryResponse | undefined>(undefined);
    const [currentField, setCurrentField] = useState<SiteWithParcelAndClientResponse | undefined>(
        undefined
    );
    const [isApplicationsOpen, setIsApplicationsOpen] = useState(false);
    const [isLinkedEntitiesOpen, setIsLinkedEntitiesOpen] = useState(false);
    const [isEditingForm, setIsEditingForm] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [isEditingMap, setIsEditingMap] = useState(state?.isEditingMap === true);
    const [selectedApplicationId, setSelectedApplicationId] = useState<string | undefined>(
        undefined
    );
    const isFirstLoad = useRef(true);

    useFloatingResourceAddContext(false);
    useNavigationCollapse(isEditingMap);

    const isPageLoading = isLoading || isFieldLoading;

    const entitiesLink: DetailsViewEntityLinkProps[] = [
        {
            entityName: currentField?.clientName ?? '',
            entityRoute: clientDetailsPage(`${currentField?.clientId}`),
            entityType: DETAIL_TYPE_CLIENT
        },
        {
            entityName: currentField?.parcelName ?? '',
            entityRoute: farmDetailsPage(`${currentField?.parcelId}`),
            entityType: DETAIL_TYPE_FARM
        },
        {
            entityName: currentField?.name ?? '',
            entityRoute: fieldDetailsPage(`${currentField?.id}`),
            entityType: DETAIL_TYPE_FIELD
        },
        {
            entityName: currentBoundary?.address?.city ?? '',
            entityRoute: undefined,
            entityType: DETAIL_TYPE_CITY
        },
        {
            entityName: currentBoundary?.address?.region ?? '',
            entityRoute: undefined,
            entityType: DETAIL_TYPE_STATE
        },
        {
            entityName: currentBoundary?.address?.zipCode ?? '',
            entityRoute: undefined,
            entityType: DETAIL_TYPE_ZIP
        }
    ];

    const handleEditMapClick = () => {
        setIsEditingMap(true);
    };

    const handleEditMapClose = () => {
        setIsEditingMap(false);
    };

    const handleMapComplete = async (result: CreateBoundaryRequest) => {
        if (!boundaryId || !currentBoundary?.name || !result) {
            setIsEditingMap(false);

            return;
        }

        await updateBoundary({
            boundary: {
                boundaryParts: result.boundaryParts,
                name: currentBoundary.name
            },
            id: boundaryId
        });

        setIsEditingMap(false);
    };

    const getCurrentBoundary = async (boundaryId: string) => {
        if (boundaryId) {
            const boundary = await fetchBoundary({ id: boundaryId });

            setCurrentBoundary(boundary ?? undefined);
        }
    };

    useEffect(() => {
        if (boundaryId) {
            getCurrentBoundary(boundaryId);
            fetchApplications({ boundaryId }).next();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boundaryId]);

    const handleSelectApplication = (application: ApplicationResponse) => {
        setSelectedApplicationId(application.id);
    };

    useEffect(() => {
        if (applications && applications.length > 0 && isFirstLoad.current) {
            isFirstLoad.current = false;
            handleSelectApplication(applications[0]);
        }
    }, [applications]);

    useEffect(() => {
        if (!currentBoundary?.siteId) {
            setCurrentField(undefined);

            return;
        }

        const getField = async () => {
            const field = await fetchField({ id: currentBoundary.siteId ?? '' });

            setCurrentField(field ?? undefined);
        };

        getField();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBoundary?.siteId]);

    const handleEditClick = () => {
        setIsEditingForm(true);
    };

    const handleToggleApplications = () => {
        setIsApplicationsOpen(previous => !previous);
    };

    const handleToggleLinkedEntities = () => {
        setIsLinkedEntitiesOpen(previous => !previous);
    };

    const handleEditClose = () => {
        if (isEditingForm) {
            setIsEditingForm(false);
            setIsClosing(false);

            if (boundaryId) {
                getCurrentBoundary(boundaryId);
            }
        }
    };

    const dropdownMenuItems = [
        {
            onClick: handleEditClick,
            text: EDIT_BOUNDARY
        }
    ];

    const handleNavigateBack = () => {
        navigate(boundariesPage);
    };

    const handleClose = () => {
        if (isEditingForm) {
            setIsClosing(true);
        } else {
            handleNavigateBack();
        }
    };

    const mapOptions: MapOptions = {
        accessToken: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string,
        center: DEFAULT_CENTER,
        mapStyle: MAP_STYLE,
        zoom: DEFAULT_ZOOM
    };

    const mapInteractionOptions: MapInteractionOptions = {
        hasDrawingControls: isEditingMap,
        hasMapControls: true
    };

    return {
        applications,
        currentBoundary,
        dropdownMenuItems,
        entitiesLink,
        handleClose,
        handleEditClose,
        handleEditMapClick,
        handleEditMapClose,
        handleMapComplete,
        handleSelectApplication,
        handleToggleApplications,
        handleToggleLinkedEntities,
        isApplicationsLoading,
        isApplicationsOpen,
        isClosing,
        isEditingForm,
        isLinkedEntitiesOpen,
        isLoading: isPageLoading,
        mapInteractionOptions,
        mapOptions,
        selectedApplicationId,
        setIsClosing
    };
};

export default useData;
